export const DREAMFLY_IMAGE_PROVIDER = 'DREAMFLY_IMAGE';
export const DREAMFLY_LLM_PROVIDER_ID = 'dreamfly_llm';
export const DREAMFLY_LLM_MODEL_STORAGE_KEY = 'dreamfly_llm_model';

export const DREAMFLY_IMAGE_QUALITY_TIERS = Object.freeze(['1K', '2K', '4K']);

export const DREAMFLY_IMAGE_MODELS = Object.freeze([
  Object.freeze({
    id: 'dreamfly_gemini_3_pro_image_preview',
    model: 'gemini-3-pro-image-preview',
    label: 'Dreamfly · gemini-3-pro-image-preview',
  }),
  Object.freeze({
    id: 'dreamfly_gemini_3_1_flash_image',
    model: 'gemini-3.1-flash-image',
    label: 'Dreamfly · gemini-3.1-flash-image',
  }),
  Object.freeze({
    id: 'dreamfly_gpt_image_2',
    model: 'gpt-image-2',
    label: 'Dreamfly · gpt-image-2',
  }),
]);

export const DREAMFLY_DEFAULT_IMAGE_MODEL_ID = 'dreamfly_gpt_image_2';

const DREAMFLY_IMAGE_MODEL_ID_BY_ALIAS = new Map([
  ['dreamfly_image', DREAMFLY_DEFAULT_IMAGE_MODEL_ID],
  ['dreamfly-image', DREAMFLY_DEFAULT_IMAGE_MODEL_ID],
  ['dreamfly_image_legacy', DREAMFLY_DEFAULT_IMAGE_MODEL_ID],
  ['dreamfly_image_2', DREAMFLY_DEFAULT_IMAGE_MODEL_ID],
  ...DREAMFLY_IMAGE_MODELS.flatMap(({ id, model }) => ([
    [id.toLowerCase(), id],
    [model.toLowerCase(), id],
  ])),
]);

export const normalizeDreamflyImageModelId = (value) => (
  DREAMFLY_IMAGE_MODEL_ID_BY_ALIAS.get(String(value || '').trim().toLowerCase()) || null
);

export const isDreamflyImageModelId = (value) => Boolean(normalizeDreamflyImageModelId(value));

export const DREAMFLY_IMAGE_API_MODEL_IDS = new Set(
  DREAMFLY_IMAGE_MODELS.map(({ model }) => model.toLowerCase()),
);

export const isDreamflyImageApiModel = (value) => (
  DREAMFLY_IMAGE_API_MODEL_IDS.has(String(value || '').trim().toLowerCase())
);

export const normalizeDreamflyImageCatalog = (payload) => {
  const envelope = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? payload
    : {};
  const rawModels = Array.isArray(payload)
    ? payload
    : Array.isArray(envelope.models)
      ? envelope.models
      : Array.isArray(envelope.data)
        ? envelope.data
        : Array.isArray(envelope.result?.models)
          ? envelope.result.models
          : Array.isArray(envelope.result?.data)
            ? envelope.result.data
            : [];

  const models = [];
  const seenIds = new Set();
  rawModels.forEach((candidate) => {
    const objectCandidate = candidate && typeof candidate === 'object' ? candidate : null;
    const requestedId = String(
      objectCandidate?.id
        || objectCandidate?.model
        || objectCandidate?.modelId
        || objectCandidate?.model_id
        || candidate
        || '',
    ).trim();
    const productType = String(
      objectCandidate?.productType || objectCandidate?.product_type || 'image',
    ).trim().toLowerCase();
    const normalizedId = normalizeDreamflyImageModelId(requestedId);
    const supported = DREAMFLY_IMAGE_MODELS.find(({ id }) => id === normalizedId);
    if (!supported || productType !== 'image' || seenIds.has(supported.model)) return;
    seenIds.add(supported.model);
    const label = String(
      objectCandidate?.displayName
        || objectCandidate?.display_name
        || objectCandidate?.modelName
        || objectCandidate?.model_name
        || objectCandidate?.name
        || supported.label,
    ).trim() || supported.label;
    models.push({ id: supported.model, label });
  });

  const defaultModel = models.some(({ id }) => id === 'gpt-image-2')
    ? 'gpt-image-2'
    : (models[0]?.id || '');
  return { models, defaultModel };
};

export const normalizeDreamflyLlmModelOption = (candidate) => {
  const objectCandidate = candidate && typeof candidate === 'object' ? candidate : null;
  const id = String(
    objectCandidate?.id
      || objectCandidate?.model
      || objectCandidate?.modelId
      || objectCandidate?.model_id
      || objectCandidate?.modelName
      || objectCandidate?.model_name
      || objectCandidate?.value
      || candidate
      || '',
  ).trim();
  if (!id || isDreamflyImageApiModel(id)) return null;
  const label = String(
    objectCandidate?.displayName
      || objectCandidate?.display_name
      || objectCandidate?.name
      || objectCandidate?.modelName
      || objectCandidate?.model_name
      || objectCandidate?.label
      || id,
  ).trim() || id;
  return { id, label };
};

export const normalizeDreamflyLlmCatalog = (payload) => {
  const envelope = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? payload
    : {};
  const rawModels = Array.isArray(payload)
    ? payload
    : Array.isArray(envelope.models)
      ? envelope.models
      : Array.isArray(envelope.data)
        ? envelope.data
        : Array.isArray(envelope.result?.models)
          ? envelope.result.models
          : Array.isArray(envelope.result?.data)
            ? envelope.result.data
            : [];

  const models = [];
  const seenIds = new Set();
  rawModels.forEach((candidate) => {
    const option = normalizeDreamflyLlmModelOption(candidate);
    if (!option || seenIds.has(option.id)) return;
    seenIds.add(option.id);
    models.push(option);
  });

  const rawDefaultModel = envelope.defaultModel
    || envelope.default_model
    || envelope.configuredModel
    || envelope.configured_model
    || envelope.result?.defaultModel
    || envelope.result?.default_model
    || '';
  const defaultOption = normalizeDreamflyLlmModelOption(rawDefaultModel);
  if (defaultOption && !seenIds.has(defaultOption.id)) {
    models.unshift(defaultOption);
  }

  return {
    models,
    defaultModel: defaultOption?.id || '',
  };
};
